import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Language } from '@shared/entities/language';
import { UserWelcomeMessageService } from '@shared/services/api/user-welcome-message.service';
import { LanguageService } from '@shared/services/language.service';
import { first, finalize, Subject, takeUntil, Subscription } from 'rxjs';

const DEFAULT_WELCOME_MESSAGE_LANGUAGE = 'it';

@Component({
  selector: 'app-credentials-section',
  templateUrl: './credentials-section.component.html',
  styleUrl: './credentials-section.component.scss'
})
export class CredentialsSectionComponent implements OnInit, OnDestroy {
  @Input() form!: FormGroup;
  @Input() isLight = true;
  @Input() isEdit = false;

  availableLanguages: Language[] = [];
  isLoadingTemplate = false;

  private destroy$ = new Subject<void>();
  private templateRequestSubscription?: Subscription;

  constructor(
    private _welcomeMessageService: UserWelcomeMessageService,
    private _languageService: LanguageService
  ) {}

  ngOnInit(): void {
    if (!this.isEdit) {
      this.loadAvailableLanguages();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.templateRequestSubscription) {
      this.templateRequestSubscription.unsubscribe();
    }
  }

  onTemplateLanguageChange(): void {
    this.loadWelcomeMessageTemplate();
  }

  private loadAvailableLanguages(): void {
    this._languageService
      .getLanguages()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response && response.languages) {
            this.availableLanguages = response.languages;
            if (this.availableLanguages.length > 0) {
              const defaultLang =
                this.availableLanguages.find((l) => l.lang === DEFAULT_WELCOME_MESSAGE_LANGUAGE)?.lang ||
                this.availableLanguages[0].lang ||
                '';
              this.form.patchValue({ templateLanguage: defaultLang });
              this.loadWelcomeMessageTemplate();
            }
          }
        },
        error: (err) => {
          console.error('Error loading available languages:', err);
        }
      });
  }

  private loadWelcomeMessageTemplate(): void {
    const lang = this.form.get('templateLanguage')?.value;

    if (this.templateRequestSubscription) {
      this.templateRequestSubscription.unsubscribe();
      this.templateRequestSubscription = undefined;
    }

    this.isLoadingTemplate = true;

    const sub = this._welcomeMessageService
      .getWelcomeMessage(lang)
      .pipe(
        first(),
        finalize(() => {
          this.isLoadingTemplate = false;
        })
      )
      .subscribe({
        next: (welcomeMessage) => {
          if (welcomeMessage) {
            const welcomeMessageTemplate = welcomeMessage.message || '';
            const welcomeMessageSubject = welcomeMessage.subject || '';
            this.form.patchValue({
              templateMail: welcomeMessageTemplate,
              welcomeSubject: welcomeMessageSubject
            });
          }
        },
        error: (err) => {
          console.error('Error loading welcome message template:', err);
        }
      });

    this.templateRequestSubscription = sub;
    sub.add(() => {
      this.templateRequestSubscription = undefined;
    });
  }
}
